import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import CohortCard from "./CohortCard";
import { useDispatch, useSelector } from "react-redux";
import {
  setCohortApplied,
  setIsEditCohort,
  setResetSearchTextForAdditionalInstitutions,
  setResetSearchTextForCities,
  setSelectedCohort,
} from "../../../../rtk-query/features/cohort/cohortSlice";
import exportimages from "../../../../assets/images/exportImages";
import { fnCheckDollarUnits } from "../../widget/widgetsCommonFunction";

const CUSTOM_COHORT_TYPE = 2;
const NO_DATA_DELAY_MS = 1000;
const NO_DATA_TITLE = "No Custom Cohorts Yet";
const NO_DATA_DESCRIPTION =
  "You haven't created any cohorts. Use filters to shape your own cohort or duplicate a suggested one to get started.";
const NativeNumber = Number;

const getAssetValue = (value) => {
  const parsedValue = Number(value);

  return typeof parsedValue === "number" && !NativeNumber.isNaN(parsedValue) ? parsedValue : 0;
};

const getCustomCohortList = (cohortList) =>
  cohortList?.filter((cohort) => cohort?.cohortType === CUSTOM_COHORT_TYPE) ?? [];

const buildRangeLabel = (filterJson) => {
  const { score: minScore, unit: minUnit } = fnCheckDollarUnits(
    getAssetValue(filterJson?.assets?.min),
  );
  const { score: maxScore, unit: maxUnit } = fnCheckDollarUnits(
    getAssetValue(filterJson?.assets?.max),
  );

  return `$${minScore?.toFixed(2)}${minUnit} – $${maxScore?.toFixed(2)}${maxUnit}`;
};

const isKeyboardSelection = (key) => key === "Enter" || key === " ";

const CohortListItem = ({ cohort, isSelected = false, onSelect, selectedRef = null }) => {
  const handleSelect = useCallback(() => {
    onSelect(cohort);
  }, [cohort, onSelect]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isKeyboardSelection(event.key)) {
        return;
      }

      event.preventDefault();
      handleSelect();
    },
    [handleSelect],
  );

  return (
    /* eslint-disable-next-line jsx-a11y/prefer-tag-over-role */
    <div
      key={cohort?.uid}
      ref={isSelected ? selectedRef : null}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      className="w-100"
      role="button"
      tabIndex={0}
    >
      <CohortCard
        uid={cohort?.uid}
        title={cohort?.name}
        subtitle={cohort?.description}
        range={buildRangeLabel(cohort?.filterJson)}
        institutions={cohort?.cohortResult?.institution_count}
        avg={cohort?.cohortResult?.cohort_avg}
        percentile={cohort?.cohortResult?.percentile}
        enableOptions
        isDefault={cohort?.isDefault}
      />
    </div>
  );
};

const CustomCohort = ({ cohortList = [] }) => {
  const dispatch = useDispatch();
  const selectedCohort = useSelector((state) => state?.cohortReducer?.selectedCohort);
  const firstSelectedRef = useRef(null);
  const validCohortList = useMemo(() => getCustomCohortList(cohortList), [cohortList]);
  const [showNoData, setShowNoData] = useState(false);

  useLayoutEffect(() => {
    if (!firstSelectedRef.current) {
      return;
    }

    requestAnimationFrame(() => {
      firstSelectedRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [selectedCohort?.uid, cohortList?.length]);

  useEffect(() => {
    if (validCohortList.length > 0) {
      setShowNoData(false);
      return undefined;
    }

    const timer = setTimeout(() => setShowNoData(true), NO_DATA_DELAY_MS);

    return () => clearTimeout(timer);
  }, [validCohortList]);

  const handleCohortSelect = useCallback((cohort) => {
    dispatch(setSelectedCohort(cohort));
    dispatch(setCohortApplied(null));
    dispatch(setIsEditCohort(false));
    dispatch(setResetSearchTextForAdditionalInstitutions(true));
    dispatch(setResetSearchTextForCities(true));
  }, [dispatch]);

  const shouldShowEmptyState = validCohortList.length === 0 && showNoData;

  return (
    <div className="list-cohort">
      {validCohortList.map((cohort) => (
        <CohortListItem
          key={cohort?.uid}
          cohort={cohort}
          isSelected={selectedCohort?.uid === cohort?.uid}
          onSelect={handleCohortSelect}
          selectedRef={firstSelectedRef}
        />
      ))}
      {shouldShowEmptyState && (
        <div className="message-container">
          <div className="no_cohort">
            <img alt="icon" src={exportimages.no_cohort} />
          </div>
          <div className="message">{NO_DATA_TITLE}</div>
          <div className="desc">{NO_DATA_DESCRIPTION}</div>
        </div>
      )}
    </div>
  );
};

CohortListItem.propTypes = {
  cohort: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    filterJson: PropTypes.shape({
      assets: PropTypes.shape({
        min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    }),
    cohortResult: PropTypes.shape({
      institution_count: PropTypes.number,
      cohort_avg: PropTypes.string,
      percentile: PropTypes.string,
    }),
    isDefault: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  selectedRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

CustomCohort.propTypes = {
  cohortList: PropTypes.array,
};

export default CustomCohort;
